import api from './api';
import { ChangeTrackingData, FieldChange, FieldComparisonResult } from '../types/article';

export interface ChangeTrackingService {
  getArticleChanges: (articleNumber: string) => Promise<ChangeTrackingData>;
  markChangesAsViewed: (articleNumber: string, userId: string) => Promise<void>;
  acceptChanges: (articleNumber: string, userId: string) => Promise<void>;
  getFieldHistory: (articleNumber: string, fieldName: string) => Promise<FieldChange[]>;
  compareFieldValues: (oldValue: any, newValue: any, fieldLabel: string) => FieldComparisonResult;
}

export const changeTrackingService: ChangeTrackingService = {
  /**
   * Hole alle Änderungen für einen Artikel
   */
  getArticleChanges: async (articleNumber: string): Promise<ChangeTrackingData> => {
    try {
      const response = await api.get(`/articles/${articleNumber}/changes`);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch article changes');
      }

      return {
        hasChanges: data.hasUnviewedChanges || false,
        changedFields: data.changedFields || [],
        changes: Object.entries(data.changes || {}).map(([field, change]: [string, any]) => ({
          field,
          fieldLabel: change.fieldLabel || field,
          oldValue: change.oldValue,
          newValue: change.newValue,
          changedAt: data.lastSubmissionDate || new Date().toISOString(),
          changedBy: 'Supplier' // Placeholder, könnte aus dem Backend kommen
        })),
        lastViewedAt: data.lastViewedByAdmin
      };
    } catch (error) {
      console.error('Error fetching article changes:', error);
      throw error;
    }
  },

  /**
   * Markiere Änderungen als angesehen
   */
  markChangesAsViewed: async (articleNumber: string, userId: string): Promise<void> => {
    try {
      const response = await api.post(`/articles/${articleNumber}/mark-changes-viewed`, { userId });
      const data = response.data;

      if (!data.success) {
        throw new Error(data.message || 'Failed to mark changes as viewed');
      }
    } catch (error) {
      console.error('Error marking changes as viewed:', error);
      throw error;
    }
  },

  /**
   * Akzeptiere Änderungen und entferne visuelle Indikatoren
   */
  acceptChanges: async (articleNumber: string, userId: string): Promise<void> => {
    try {
      const response = await api.post(`/articles/${articleNumber}/accept-changes`, { userId });
      const data = response.data;

      if (!data.success) {
        throw new Error(data.message || 'Failed to accept changes');
      }
    } catch (error) {
      console.error('Error accepting changes:', error);
      throw error;
    }
  },

  /**
   * Hole Historie für ein spezifisches Feld
   */
  getFieldHistory: async (articleNumber: string, fieldName: string): Promise<FieldChange[]> => {
    try {
      console.log(`[CHANGE_TRACKING_SERVICE] Getting field history for ${articleNumber}/${fieldName}`);
      
      // Encode field name for URL
      const encodedFieldName = encodeURIComponent(fieldName);
      const url = `/articles/${articleNumber}/field-history/${encodedFieldName}`;
      console.log(`[CHANGE_TRACKING_SERVICE] Requesting URL: ${url}`);
      
      const response = await api.get(url);
      const data = response.data;
      
      console.log(`[CHANGE_TRACKING_SERVICE] Response:`, data);

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch field history');
      }

      return data.fieldHistory.map((entry: any) => ({
        field: entry.field,
        fieldLabel: entry.fieldLabel || entry.field,
        oldValue: entry.oldValue,
        newValue: entry.newValue,
        changedAt: entry.changedAt,
        changedBy: entry.changedBy
      }));
    } catch (error) {
      console.error('Error fetching field history:', error);
      throw error;
    }
  },

  /**
   * Vergleiche Feldwerte und gib Änderungen zurück
   */
  compareFieldValues: (oldValue: any, newValue: any, fieldLabel: string): FieldComparisonResult => {
    const hasChanges = JSON.stringify(oldValue) !== JSON.stringify(newValue);
    
    if (!hasChanges) {
      return { hasChanges: false, changes: {} };
    }

    return {
      hasChanges: true,
      changes: {
        [fieldLabel]: {
          oldValue,
          newValue,
          fieldLabel
        }
      }
    };
  }
};

export default changeTrackingService;
